// Estado de cada paso del menú de acciones de una OT en Programas: qué ya se completó, qué va en
// curso y cuál es el siguiente paso sugerido. Función pura: el menú solo pinta el resultado.
//
// Estados: 'done' (completado), 'progress' (en curso), 'pending' (disponible, sin empezar),
// 'blocked' (aún no se puede: lleva el motivo) y 'action' (acción sin "completado", ej. descargar).

export const WORKFLOW_SECTIONS = [
  { key: 'prep', label: '1 · Preparación', steps: ['edit', 'assigned', 'captureUipc'] },
  { key: 'field', label: '2 · Campo', steps: ['evaluation'] },
  { key: 'file', label: '3 · Expediente', steps: ['validate', 'files'] },
  { key: 'gen', label: '4 · Generación', steps: ['generateRic', 'generatePipc', 'generateActa', 'generateCarta', 'generateConstancias'] },
  { key: 'delivery', label: '5 · Entrega', steps: ['download', 'downloadReport', 'delivery'] },
];

const STEP_LABELS = {
  edit: 'Iniciar Programa',
  assigned: 'Asignado y programado',
  captureUipc: 'Captura UIPC',
  evaluation: 'Evaluación de Inspección',
  validate: 'Validar Campos',
  files: 'Subir Archivos',
  generateRic: 'Generar RIC',
  generatePipc: 'Generar PIPC',
  generateActa: 'Generar Acta Constitutiva',
  generateCarta: 'Generar Carta de Corresponsabilidad',
  generateConstancias: 'Generar Constancias',
  download: 'Descargar Archivo PIPC',
  downloadReport: 'Descargar Reporte de Inspección',
  delivery: 'Entrega al cliente',
  clientReceipt: 'Recepción del cliente',
};

const ACTION_STEPS = new Set(['download', 'downloadReport']);

const QA_LABEL = {
  PENDIENTE_REVISION: 'en revisión de calidad',
  REVISION_1_APROBADA: '1ª revisión aprobada',
  REVISION_1_RECHAZADA: '1ª revisión con observaciones',
  REVISION_2_APROBADA: 'aprobado por calidad',
  REVISION_2_RECHAZADA: '2ª revisión con observaciones',
};

const shortDate = (value) => (value
  ? new Date(String(value).length === 10 ? `${value}T12:00:00` : value).toLocaleDateString('es-MX', { day: '2-digit', month: 'short' })
  : '');

// x de y entes que cumplen -> done / progress / pending
const countState = (done, total) => {
  if (total === 0) return 'pending';
  if (done >= total) return 'done';
  return done > 0 ? 'progress' : 'pending';
};

const missingNames = (entes, predicate) => entes.filter((e) => !predicate(e)).map((e) => e.name).join(', ');

/**
 * @param input.programStarted   PIP iniciado (pipStatus)
 * @param input.programComplete  PIP con sus datos completos (pipCompletionStatus)
 * @param input.entes            [{ name, branchId, asignado, uipc, evaluacion }] -- avance por ente (summary)
 * @param input.readiness        [{ branchId, pipcReady, pipcPercentage, docsUploaded, docsRequired, docsReady }]
 * @param input.ricReady / pipcReady  hay al menos un ente listo para generar (readinessMap)
 * @param input.qaRound1Approved habilita Acta/Carta
 * @param input.workOrderCompleted habilita descargar/enviar
 * @param input.status           respuesta de GET /work-orders/{id}/workflow-status (puede venir null)
 * @param input.permissions      { canPrepare, canDeliver }
 */
export const buildWorkflowSteps = (input) => {
  const {
    programStarted, programComplete, entes = [], readiness = [], ricReady, pipcReady,
    qaRound1Approved, workOrderCompleted, status, permissions = {},
  } = input;
  const total = entes.length;
  const readinessFor = (branchId) => readiness.find((r) => String(r.branchId ?? null) === String(branchId ?? null));
  const statusEntes = status?.entities || [];
  const docFor = (key) => statusEntes.map((e) => e.documents?.[key]).filter(Boolean);
  const notStarted = 'Primero inicia el Programa';
  const steps = {};

  // acompanante === false: falta la persona que acompañó en el recorrido (obligatoria por ente)
  const missingCompanion = entes.filter((e) => e.acompanante === false);
  steps.edit = permissions.canPrepare === false
    ? { state: 'blocked', detail: 'Sin permiso para iniciarlo' }
    : programStarted
      ? (!programComplete
        ? { state: 'progress', detail: 'Iniciado · faltan datos generales' }
        : missingCompanion.length
          ? { state: 'progress', detail: 'Iniciado · falta quién acompañó en el recorrido', missing: missingCompanion.map((e) => e.name).join(', ') }
          : { state: 'done', detail: 'Programa iniciado' })
      : { state: 'pending', detail: 'Aún no se inicia' };

  const assignedDone = entes.filter((e) => e.asignado).length;
  steps.assigned = { state: countState(assignedDone, total), detail: total ? `${assignedDone}/${total} entes` : '',
    missing: missingNames(entes, (e) => e.asignado) };

  const uipcDone = entes.filter((e) => e.uipc).length;
  steps.captureUipc = permissions.canPrepare === false
    ? { state: 'blocked', detail: 'Sin permiso para capturarla' }
    : { state: countState(uipcDone, total), detail: total ? `${uipcDone}/${total} entes` : '' ,
      missing: missingNames(entes, (e) => e.uipc) };

  const evalDone = entes.filter((e) => e.evaluacion).length;
  steps.evaluation = !programStarted
    ? { state: 'blocked', detail: notStarted }
    : { state: countState(evalDone, total), detail: total ? `${evalDone}/${total} entes` : '',
      missing: missingNames(entes, (e) => e.evaluacion) };

  const readyEntes = entes.filter((e) => readinessFor(e.branchId)?.pipcReady).length;
  const avgPct = total
    ? Math.round(entes.reduce((sum, e) => sum + (readinessFor(e.branchId)?.pipcPercentage ?? 0), 0) / total)
    : 0;
  steps.validate = !programStarted
    ? { state: 'blocked', detail: notStarted }
    : { state: countState(readyEntes, total), detail: readyEntes >= total && total ? 'Campos completos' : `Campos ${avgPct}%`,
      missing: missingNames(entes, (e) => readinessFor(e.branchId)?.pipcReady) };

  const docsUp = entes.reduce((sum, e) => sum + (readinessFor(e.branchId)?.docsUploaded ?? 0), 0);
  const docsReq = entes.reduce((sum, e) => sum + (readinessFor(e.branchId)?.docsRequired ?? 0), 0);
  const docsReadyEntes = entes.filter((e) => readinessFor(e.branchId)?.docsReady).length;
  steps.files = { state: countState(docsReadyEntes, total), detail: docsReq ? `${docsUp}/${docsReq} documentos` : '',
    missing: missingNames(entes, (e) => readinessFor(e.branchId)?.docsReady) };

  const generated = (key, ready, notReadyReason) => {
    const docs = docFor(key);
    if (docs.length === 0) {
      return ready ? { state: 'pending', detail: 'Listo para generar' } : { state: 'blocked', detail: notReadyReason };
    }
    const qa = docs.map((d) => QA_LABEL[d.qaStatus]).filter(Boolean);
    const latest = docs.map((d) => d.generatedAt).filter(Boolean).sort().pop();
    const base = `${docs.length}/${statusEntes.length || docs.length} generado${docs.length === 1 ? '' : 's'}${latest ? ` · ${shortDate(latest)}` : ''}`;
    return { state: countState(docs.length, statusEntes.length || docs.length), detail: qa.length ? `${base} · ${qa[0]}` : base };
  };
  steps.generateRic = generated('REPORTE_INSPECCION', ricReady, 'Completa los campos requeridos (Validar Campos)');
  steps.generatePipc = generated('PIPC', pipcReady, 'Completa los campos requeridos (Validar Campos)');
  steps.generateActa = generated('ACTA_CONSTITUTIVA_UIPC', qaRound1Approved, 'Se habilita después de la 1ª ronda de revisión de calidad');
  steps.generateCarta = generated('CARTA_CORRESPONSABILIDAD', qaRound1Approved, 'Se habilita después de la 1ª ronda de revisión de calidad');

  const constancias = status?.constancias;
  steps.generateConstancias = !programStarted
    ? { state: 'blocked', detail: 'Primero inicia el Programa (captura la UIPC)' }
    : !constancias
      ? { state: 'pending', detail: '' }
      : constancias.expected === 0
        ? { state: 'pending', detail: 'Sin capacitación programada o sin integrantes de UIPC' }
        : { state: countState(constancias.generated, constancias.expected), detail: `${constancias.generated}/${constancias.expected} constancias` };

  const downloadable = workOrderCompleted
    ? { state: 'action', detail: '' }
    : { state: 'blocked', detail: 'Se habilita al completar la orden' };
  steps.download = downloadable;
  steps.downloadReport = downloadable;

  const stages = statusEntes.map((e) => e.deliveryStage).filter(Boolean);
  const delivered = stages.filter((s) => s === 'ENTREGADA').length;
  const released = stages.filter((s) => s === 'LIBERADA').length;
  // Sin permiso de entregar (Ejecutivo) se muestra igual, con su avance real, pero sin acción:
  // si se ocultaba, el menú decía "Flujo completo" con la entrega pendiente.
  const deliveryState = !stages.length
      ? { state: 'pending', detail: 'Liberar documentos y registrar acuse' }
      : delivered === stages.length
        ? { state: 'done', detail: `${delivered}/${stages.length} entregados` }
        : released + delivered > 0
          ? { state: 'progress', detail: `${delivered}/${stages.length} entregados${released ? ` · ${released} esperando acuse` : ''}` }
          : { state: 'pending', detail: 'Liberar documentos y registrar acuse' };
  steps.delivery = permissions.canDeliver === false && deliveryState.state !== 'done'
    ? { ...deliveryState, state: deliveryState.state === 'progress' ? 'progress' : 'blocked',
      detail: `${deliveryState.detail} · la hace Coordinación o Calidad` }
    : deliveryState;

  const ordered = WORKFLOW_SECTIONS.flatMap((s) => s.steps);
  const nextKey = ordered.find((k) => steps[k] && (steps[k].state === 'pending' || steps[k].state === 'progress')) || null;

  // Las descargas son acciones (nunca quedan "completadas"): no cuentan en el avance
  const sections = WORKFLOW_SECTIONS.map((s) => {
    const own = s.steps.filter((k) => steps[k] && !ACTION_STEPS.has(k) && steps[k].state !== 'action');
    return { ...s, done: own.filter((k) => steps[k].state === 'done').length, total: own.length };
  });
  // "Flujo completo" solo si de verdad todo lo que cuenta está completado
  const allDone = sections.every((s) => s.done === s.total);
  const waitingKey = allDone ? null
    : ordered.find((k) => steps[k] && !ACTION_STEPS.has(k) && !['done', 'action'].includes(steps[k].state));
  const waiting = waitingKey ? { key: waitingKey, label: STEP_LABELS[waitingKey], detail: steps[waitingKey].detail } : null;

  const next = nextKey ? {
    key: nextKey,
    label: STEP_LABELS[nextKey],
    hint: steps[nextKey].missing ? `falta en ${steps[nextKey].missing}` : steps[nextKey].detail,
  } : null;

  return { steps, sections, next, allDone, waiting };
};

// Etapas que cuentan para el avance (las mismas del menú de Programas, sin las de solo descargar)
export const ENTE_STAGE_KEYS = ['edit', 'assigned', 'captureUipc', 'evaluation', 'validate', 'files',
  'generateRic', 'generatePipc', 'generateActa', 'generateCarta', 'generateConstancias', 'delivery', 'clientReceipt'];
export const STAGE_LABELS = STEP_LABELS;
// Las que se cumplen para toda la OT a la vez (no por ente)
export const ORDER_LEVEL_STAGES = new Set([]);

/**
 * Avance POR ENTE con las mismas etapas y criterios del menú de Programas (buildWorkflowSteps):
 * para el Tablero de Seguimiento. Recibe los mismos datos; devuelve, por ente, qué etapas cumple.
 * @returns [{ branchId, name, stages: [{ key, label, done, orderLevel }], completed, total, percentage }]
 */
export const buildEnteStages = (input) => {
  const { programStarted, programComplete, entes = [], readiness = [], status } = input;
  const readinessFor = (branchId) => readiness.find((r) => String(r.branchId ?? null) === String(branchId ?? null));
  const statusFor = (branchId) => (status?.entities || []).find((e) => String(e.branchId ?? null) === String(branchId ?? null));
  return entes.map((e) => {
    const r = readinessFor(e.branchId);
    const st = statusFor(e.branchId);
    const doc = (key) => !!st?.documents?.[key];
    const c = st?.constancias;
    const done = {
      edit: !!(programStarted && programComplete && e.acompanante !== false),
      assigned: !!e.asignado,
      captureUipc: !!e.uipc,
      evaluation: !!e.evaluacion,
      validate: !!r?.pipcReady,
      files: !!r?.docsReady,
      generateRic: doc('REPORTE_INSPECCION'),
      generatePipc: doc('PIPC'),
      generateActa: doc('ACTA_CONSTITUTIVA_UIPC'),
      generateCarta: doc('CARTA_CORRESPONSABILIDAD'),
      generateConstancias: !!(c && c.expected > 0 && c.generated >= c.expected),
      delivery: ['LIBERADA', 'ENTREGADA'].includes(st?.deliveryStage),
      clientReceipt: st?.deliveryStage === 'ENTREGADA',
    };
    const stages = ENTE_STAGE_KEYS.map((key) => ({ key, label: STEP_LABELS[key], done: done[key], orderLevel: ORDER_LEVEL_STAGES.has(key) }));
    const completed = stages.filter((x) => x.done).length;
    return {
      branchId: e.branchId ?? null, name: e.name, stages, completed, total: stages.length,
      percentage: Math.round((completed * 100) / stages.length),
    };
  });
};

// Datos generales del Programa (PIP) que lo dan por "iniciado y completo" -- mismo criterio en
// Programas y en el Tablero de Seguimiento.
export const PIP_ESSENTIAL_FIELDS = ['preparedBy', 'reviewedBy', 'approvedBy', 'serviceScope', 'methodology', 'estimatedDuration'];
export const isPipComplete = (pip) => !!pip && PIP_ESSENTIAL_FIELDS.every((f) => pip[f] && pip[f].toString().trim().length > 0);
